/**
 * Skeleton loaders for the course pages (7 Oct 2026) — the shape of what is coming, in place of a
 * spinner. Built from the one shimmering block (`Skeleton`, slowed to a 2.4s sweep in index.css),
 * so every page loads the same way.
 */
import { Skeleton } from '@/components/org/orgUi'

/** Course / collection cards in a grid: a picture, two title lines and a chip. */
export function CardGridSkeleton({ count = 8, className = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 gap-y-6', tall = false }: {
  count?: number; className?: string; tall?: boolean
}) {
  return (
    <div role="status" aria-label="Loading" className={className}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          <Skeleton h="auto" r={0} style={{ aspectRatio: tall ? '4 / 3' : '16 / 10' }} />
          <div className="flex flex-col gap-2 p-4">
            <Skeleton w={`${78 - (i % 3) * 12}%`} h={12} />
            <Skeleton w={`${50 - (i % 2) * 10}%`} h={12} />
            <Skeleton w={70} h={22} r={6} style={{ marginTop: 4 }} />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Home: a heading and a row of recent courses, then the Library row. */
export function HomeSkeleton() {
  return (
    <div role="status" aria-label="Loading" className="flex flex-col gap-10">
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between"><Skeleton w={140} h={15} /><Skeleton w={56} h={12} /></div>
        <CardGridSkeleton count={4} />
      </section>
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between"><Skeleton w={110} h={15} /><Skeleton w={56} h={12} /></div>
        <CardGridSkeleton count={3} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" tall />
      </section>
    </div>
  )
}

/** A page with a heading block, then rows — a collection's courses, a course's languages. */
export function HeaderRowsSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading" className="flex flex-col gap-6">
      <div className="flex items-center gap-4">
        <Skeleton w={64} h={64} r={14} />
        <div className="flex flex-1 flex-col gap-2"><Skeleton w="40%" h={18} /><Skeleton w="60%" h={12} /></div>
      </div>
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center gap-4 border-b border-gray-50 px-5 py-4">
            <Skeleton w={88} h={56} r={8} />
            <div className="flex flex-1 flex-col gap-2"><Skeleton w={`${45 - i * 5}%`} h={13} /><Skeleton w={`${30 - i * 3}%`} h={11} /></div>
            <Skeleton w={70} h={24} r={999} />
            <Skeleton w={28} h={28} r={8} />
          </div>
        ))}
      </div>
    </div>
  )
}

/** Template cards: a name, three flow lines and the chips. */
export function TemplateCardsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div role="status" aria-label="Loading" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4">
          <Skeleton w={`${70 - i * 10}%`} h={13} />
          <Skeleton w="35%" h={10} />
          <div className="mt-1 flex flex-col gap-2">
            {[0, 1, 2].map(k => <div key={k} className="flex items-center gap-2"><Skeleton w={16} h={16} r={8} /><Skeleton w={`${60 - k * 12}%`} h={10} /></div>)}
          </div>
          <div className="mt-1 flex gap-2"><Skeleton w={60} h={20} r={999} /><Skeleton w={50} h={20} r={999} /></div>
        </div>
      ))}
    </div>
  )
}
